import type { EntityKind, EntityProjection } from "../../domain/entities.ts";
import { restoreStoredProjection } from "../sync/document-projection.ts";

export interface StoredEntity {
  id: string;
  rootId: string;
  kind: EntityKind;
  snapshot: Uint8Array;
  projection: EntityProjection;
  updatedAt: number;
}

export interface OutboxChange {
  hash: string;
  entityId: string;
  rootId: string;
  kind: EntityKind;
  bytes: string;
  projection: EntityProjection;
  createdAt: number;
  batchId?: string;
}

const DB_NAME = "weirdo-local";
const DB_VERSION = 1;
let connection: Promise<IDBDatabase> | undefined;

function database(): Promise<IDBDatabase> {
  return connection ||= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      const entities = db.createObjectStore("entities", { keyPath: "id" });
      entities.createIndex("rootId", "rootId");
      db.createObjectStore("outbox", { keyPath: "hash" });
      db.createObjectStore("meta", { keyPath: "key" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function putEntity(entity: StoredEntity): Promise<void> {
  const tx = (await database()).transaction("entities", "readwrite");
  tx.objectStore("entities").put(entity);
  await complete(tx);
}

export async function putTodoChangesAtomically(items: Array<{ entity: StoredEntity; outbox: OutboxChange }>): Promise<void> {
  const tx = (await database()).transaction(["entities", "outbox"], "readwrite");
  for (const item of items) {
    tx.objectStore("entities").put(item.entity);
    tx.objectStore("outbox").put(item.outbox);
  }
  await complete(tx);
}

export async function getEntity(id: string): Promise<StoredEntity | undefined> {
  const tx = (await database()).transaction("entities");
  const entity = await requestValue<StoredEntity | undefined>(tx.objectStore("entities").get(id));
  return entity ? restoreStoredProjection(entity) : undefined;
}

export async function listEntities(rootId?: string): Promise<StoredEntity[]> {
  const tx = (await database()).transaction("entities");
  const store = tx.objectStore("entities");
  const entities = await requestValue<StoredEntity[]>(rootId ? store.index("rootId").getAll(rootId) : store.getAll());
  return entities.map(restoreStoredProjection);
}

export async function addOutbox(change: OutboxChange): Promise<void> {
  const tx = (await database()).transaction("outbox", "readwrite");
  tx.objectStore("outbox").put(change);
  await complete(tx);
}

export async function listOutbox(): Promise<OutboxChange[]> {
  const tx = (await database()).transaction("outbox");
  const changes = await requestValue<OutboxChange[]>(tx.objectStore("outbox").getAll());
  return changes.sort((left, right) => left.createdAt - right.createdAt);
}

/** Keep a rejected edit and its compensation in one atomic server batch. */
export async function groupOutboxChanges(hashes: string[], batchId: string): Promise<void> {
  if (!hashes.length || hashes.length > 250) throw new Error("Invalid recovery batch size");
  const tx = (await database()).transaction("outbox", "readwrite");
  const store = tx.objectStore("outbox");
  const selected = new Set(hashes);
  const changes = await requestValue<OutboxChange[]>(store.getAll());
  for (const change of changes) if (selected.has(change.hash)) store.put({ ...change, batchId });
  await complete(tx);
}

export async function acknowledge(hashes: string[]): Promise<void> {
  const tx = (await database()).transaction("outbox", "readwrite");
  const store = tx.objectStore("outbox");
  hashes.forEach(hash => store.delete(hash));
  await complete(tx);
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const tx = (await database()).transaction("meta");
  const row = await requestValue<{ key: string; value: T } | undefined>(tx.objectStore("meta").get(key));
  return row?.value ?? fallback;
}

export async function setMeta<T>(key: string, value: T): Promise<void> {
  const tx = (await database()).transaction("meta", "readwrite");
  tx.objectStore("meta").put({ key, value });
  await complete(tx);
}

export async function clearLocalData(actorId: string): Promise<void> {
  const tx = (await database()).transaction(["entities", "outbox", "meta"], "readwrite");
  tx.objectStore("entities").clear();
  tx.objectStore("outbox").clear();
  const meta = tx.objectStore("meta");
  meta.clear();
  meta.put({ key: "actorId", value: actorId });
  await complete(tx);
}

function complete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
